import { Bounds } from '@ardoq/graph';
import { BlocksNode } from '../types';
import { RenderContext } from './types';
import {
  AQBLOCKS_LABEL_LINESIZE,
  AQBLOCKS_SEP,
  AQBLOCKS_SUBLABEL_BORDERCLAMP,
  AQBLOCKS_SUBLABEL_CORNERCLAMP,
  AQBLOCKS_SUBLABEL_LINESIZE,
} from '../consts';
import {
  rectCenter,
  rectHeight,
  rectWidth,
} from 'tabview/visualizations/misc/geometry';
import { fontAwesomeIcons } from '@ardoq/icons';
import { svgImage } from 'tabview/canvasRendering/canvasResolvedImages';
import { firstVisibleSublabel } from '../util';

export const drawFontAwesomeIcon = (
  id: string,
  bounds: Bounds,
  background: string,
  params: RenderContext
) => {
  const { ctx } = params;

  const rc = params.projection.projectBoundsSnapped(bounds);
  const size = Math.min(rectWidth(rc), rectHeight(rc));
  const fillStyle = params.contrast(background);

  ctx.font = `${size}px FontAwesome`;
  ctx.fillStyle = fillStyle;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(fontAwesomeIcons[id], ...rectCenter(rc));
};

export const drawSvgIcon = (
  id: string,
  bounds: Bounds,
  background: string,
  params: RenderContext
) => {
  const rc = params.projection.projectBounds(bounds);
  const size = Math.round(Math.min(rectWidth(rc), rectHeight(rc)));
  const center = rectCenter(rc);

  const fill = 'green';
  const stroke = '';
  const color = params.contrast(background);
  const negativeColor = 'yellow';
  const img = svgImage(
    `${id}~~${color}~~${fill}~~${stroke}~~100%~~100%~~${negativeColor}`
  );

  params.ctx.drawImage(
    img,
    center[0] - 0.5 * size,
    center[1] - 0.5 * size,
    size,
    size
  );
};

// draws a wrapped label without a background. have text color and font set before calling this
export const drawLabel = (
  text: string[],
  labelColor: string | undefined,
  fillColor: string,
  bounds: Bounds,
  params: RenderContext
) => {
  const { ctx, projection } = params;
  const lineSize = AQBLOCKS_LABEL_LINESIZE;

  if (params.labelAlpha <= 0) {
    return;
  }

  if (labelColor) {
    const rc = projection.projectBoundsSnapped(bounds);
    const cornerRadius = Math.min(
      rectHeight(rc) / 4,
      params.sublabelCornerRadius
    );

    ctx.beginPath();
    if (cornerRadius > AQBLOCKS_SUBLABEL_CORNERCLAMP[0]) {
      ctx.roundRect(rc[0], rc[1], rectWidth(rc), rectHeight(rc), cornerRadius);
    } else {
      ctx.rect(rc[0], rc[1], rectWidth(rc), rectHeight(rc));
    }

    ctx.fillStyle = params.blend(0.3, fillColor, labelColor);
    ctx.strokeStyle = params.blend(0.9, fillColor, labelColor);
    ctx.lineWidth = params.sublabelBorderThickness;
    ctx.fill();
    ctx.stroke();
  }

  ctx.textBaseline = 'middle';
  ctx.font = params.labelFont;

  for (let i = 0; i < text.length; ++i) {
    const top = bounds[1] + i * lineSize;
    const rc = projection.projectBoundsSnapped([
      bounds[0],
      top,
      bounds[2],
      top + lineSize,
    ]);

    params.ctx.fillStyle = params.labelColor(fillColor);
    // probably some cleverness to draw a standin if the text is teeny-tiny

    switch (ctx.textAlign) {
      case 'center':
        ctx.fillText(text[i], ...rectCenter(rc));
        break;

      case 'left':
      case 'start':
        ctx.fillText(text[i], rc[0], 0.5 * (rc[1] + rc[3]));
        break;

      case 'right':
      case 'end':
        ctx.fillText(text[i], rc[2], 0.5 * (rc[1] + rc[3]));
        break;
    }
  }
};

export const measureSublabel = (text: string[], params: RenderContext) => {
  let w = 0;

  for (let i = 0; i < text.length; ++i) {
    w = Math.max(w, params.ctx.measureText(text[i]).width);
  }

  return w;
};

/* draw a single wrapped text with background have text alignment and font set before calling this */
const drawSublabel = (
  sublabel: string[],
  sublabelColor: string | undefined,
  fillColor: string,
  bounds: Bounds,
  params: RenderContext
) => {
  const ctx = params.ctx;
  const rc = params.projection.projectBounds(bounds);
  const cornerRadius = Math.min(
    rectHeight(rc) / 4,
    params.sublabelCornerRadius
  );

  let x = NaN;

  const lineSize = AQBLOCKS_SUBLABEL_LINESIZE / params.pixel();
  const sep = AQBLOCKS_SEP / params.pixel();
  const textAlign = ctx.textAlign;

  switch (ctx.textAlign) {
    case 'start':
    case 'left':
      rc[2] = rc[0] + sep + measureSublabel(sublabel, params) + sep;
      x = rc[0] + sep;
      ctx.textAlign = 'left';
      break;

    case 'center':
      x = (rc[0] + rc[2]) / 2;
      ctx.textAlign = 'center';
      break;

    case 'end':
    case 'right':
      rc[0] = rc[2] - measureSublabel(sublabel, params) - sep;
      x = rc[0];
      ctx.textAlign = 'left';
      break;
  }

  /* label background and border */

  const sublabelFill = params.sublabelFill(sublabelColor, fillColor);
  const sublabelStroke = params.border(sublabelFill);

  if (sublabelFill || sublabelStroke) {
    ctx.beginPath();

    if (cornerRadius > AQBLOCKS_SUBLABEL_CORNERCLAMP[0]) {
      ctx.roundRect(rc[0], rc[1], rectWidth(rc), rectHeight(rc), cornerRadius);
    } else {
      ctx.rect(rc[0], rc[1], rectWidth(rc), rectHeight(rc));
    }

    if (sublabelFill) {
      ctx.fillStyle = sublabelFill;
      ctx.fill();
    }

    if (
      sublabelStroke &&
      params.sublabelBorderThickness > AQBLOCKS_SUBLABEL_BORDERCLAMP[0]
    ) {
      ctx.lineWidth = params.sublabelBorderThickness;
      ctx.strokeStyle = sublabelStroke;
      ctx.stroke();
    }
  }

  if (params.sublabelAlpha > 0) {
    const sublabelText = params.labelColor(
      sublabelFill ? sublabelFill : fillColor
    );

    ctx.fillStyle = sublabelText;
    ctx.textBaseline = 'middle';

    for (let i = 0; i < sublabel.length; ++i) {
      const y = rc[1] + (i + 0.5) * lineSize;

      ctx.fillText(sublabel[i], x, y);
    }

    ctx.textAlign = textAlign;
  }
};

/* have text alignment set before calling this */
export const drawSublabels = (
  node: BlocksNode,
  labelColors: (string | undefined)[],
  fillColor: string,
  bounds: Bounds,
  params: RenderContext
) => {
  const { displayLabels } = node;
  const first = firstVisibleSublabel(node);
  let top = bounds[1];
  const { ctx } = params;

  ctx.textBaseline = 'middle';
  ctx.font = params.sublabelFont;

  for (let i = first; i < displayLabels.length; ++i) {
    const displayLabel = displayLabels[i];
    const labelColor = i < labelColors.length ? labelColors[i] : undefined;
    const h = displayLabel.length * AQBLOCKS_SUBLABEL_LINESIZE;

    if (h > 0) {
      drawSublabel(
        displayLabel,
        labelColor,
        fillColor,
        [bounds[0], top, bounds[2], top + h],
        params
      );
      top += h + AQBLOCKS_SEP;
    }
  }
};
